import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaTrash, FaCheck, FaTimes, FaChevronLeft, FaExclamationTriangle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

function getFinYearId() {
  try {
    const direct = localStorage.getItem("finYearId");
    if (direct) return String(direct);
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.finYearId) return String(admin.finYearId);
  } catch (_) {}
  return "";
}

function tenantHeaders() {
  const finYearId = getFinYearId();
  return finYearId ? { "x-fin-year-id": finYearId } : {};
}

function tenantParams(extra = {}) {
  const finYearId = getFinYearId();
  return finYearId ? { ...extra, finYearId } : extra;
}

/**
 * DepartmentListing.jsx (Listing + Add/Edit on same page)
 *
 * Backend:
 *  GET  /api/departmentMaster/dept_dropdown
 *  GET  /api/departmentMaster/dept_list?page=&pageSize=&search=&deptId=
 *  POST /api/departmentMaster/dept_delete_selected  { ids: [] }
 *  POST /api/departmentMaster/dept_save            { deptId, deptName, userId }
 */

const DepartmentListing = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  // ---------------- LIST STATE ----------------
  const [deptOptions, setDeptOptions] = useState([]);
  const [selectedDeptId, setSelectedDeptId] = useState(""); // "" = Select

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  // ---------------- FORM (ADD/EDIT) ----------------
  const [showForm, setShowForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  const [formData, setFormData] = useState({ deptName: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // ---------------- DELETE CONFIRM ----------------
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);

  const getAdminId = () => {
    try {
      const admin = JSON.parse(localStorage.getItem("admin"));
      return Number(admin?.id || 0);
    } catch {
      return 0;
    }
  };

  // debounce search typing
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  // ---------------- API ----------------
  const fetchDropdown = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/departmentMaster/dept_dropdown`, {
        headers: tenantHeaders(),
        params: tenantParams(),
      });
      if (!res.data?.success) return;
      setDeptOptions(res.data.list || []);
    } catch (err) {
      console.error("dept_dropdown error:", err);
    }
  };

  const fetchList = async (page = 1, search = "", deptId = "") => {
    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/departmentMaster/dept_list`, {
        headers: tenantHeaders(),
        params: tenantParams({ page, pageSize, search, deptId: deptId || "" }),
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load departments");
        return;
      }

      const list = res.data.ListData || [];
      const pg = res.data.pagination || {};

      const mapped = list.map((row) => ({
        id: row.deptid,
        sNo: row.rowno,
        name: row.deptname,
        selected: false,
      }));

      setRecords(mapped);
      setTotalRecords(pg.total ?? 0);
      setTotalPages(pg.totalPages ?? 1);
      setCurrentPage(pg.page ?? page);
    } catch (err) {
      console.error("dept_list error:", err);
      toast.error("Failed to load departments");
    } finally {
      setLoading(false);
    }
  };

  // initial
  useEffect(() => {
    fetchDropdown();
    fetchList(1, "", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // search/filter changes reload list (only when listing is visible)
  useEffect(() => {
    if (showForm) return;
    fetchList(1, debouncedSearch, selectedDeptId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    if (showForm) return;
    fetchList(1, debouncedSearch, selectedDeptId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeptId]);

  // ---------------- Pagination ----------------
  const handlePageChange = (page) => {
    if (!page || page === "..." || page === currentPage) return;
    fetchList(page, debouncedSearch, selectedDeptId);
  };

  const renderPageNumbers = useMemo(() => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(1);

    const left = Math.max(2, currentPage - 2);
    const right = Math.min(totalPages - 1, currentPage + 2);

    if (left > 2) pages.push("...");

    for (let i = left; i <= right; i++) pages.push(i);

    if (right < totalPages - 1) pages.push("...");

    pages.push(totalPages);

    return pages;
  }, [totalPages, currentPage]);

  // ---------------- Form helpers ----------------
  const openAddForm = () => {
    setShowForm(true);
    setIsEdit(false);
    setEditId(null);
    setFormData({ deptName: "" });
    setErrors({});
  };

  const openEditForm = (record) => {
    setShowForm(true);
    setIsEdit(true);
    setEditId(record.id);
    setFormData({ deptName: record.name || "" });
    setErrors({});
  };

  const closeForm = () => {
    setShowForm(false);
    setIsEdit(false);
    setEditId(null);
    setFormData({ deptName: "" });
    setErrors({});
  };

  const handleFormChange = (value) => {
    setFormData({ deptName: value });
    if (errors.deptName) setErrors({});
  };

  const validateForm = () => {
    const e = {};
    if (!String(formData.deptName || "").trim()) e.deptName = "Department Name is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please enter Department Name");
      return;
    }

    const userId = getAdminId();

    if (!userId) {
      toast.error("UserId missing! Please login again.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        deptId: isEdit && editId ? editId : 0,
        deptName: formData.deptName.trim(),
        userId,
      };

      const res = await axios.post(`${BASE_URL}/api/departmentMaster/dept_save`, payload, {
        headers: tenantHeaders(),
      });
      if (!res.data?.success) {
        toast.error(res.data?.message || "Save failed");
        return;
      }

      toast.success(res.data?.message || "Saved Successfully");

      await fetchDropdown();
      await fetchList(1, debouncedSearch, selectedDeptId);
      closeForm();
    } catch (err) {
      console.error("dept_save error:", err);
      toast.error(err.response?.data?.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  // ---------------- Bulk Delete ----------------
  const handleSelectRecord = (id, checked) => {
    setRecords((prev) => prev.map((r) => (r.id === id ? { ...r, selected: checked } : r)));
  };

  const openDeleteConfirm = () => {
    const selectedIds = records.filter((r) => r.selected).map((r) => r.id);

    if (selectedIds.length === 0) {
      toast.error("Select any one Department");
      return;
    }

    setPendingDeleteIds(selectedIds);
    setConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (isDeleting) return;
    setConfirmOpen(false);
    setPendingDeleteIds([]);
  };

  const confirmDeleteSelected = async () => {
    if (pendingDeleteIds.length === 0) return;

    try {
      setIsDeleting(true);

      const res = await axios.post(
        `${BASE_URL}/api/departmentMaster/dept_delete_selected`,
        { ids: pendingDeleteIds },
        { headers: tenantHeaders() }
      );

      if (res.data?.success) {
        toast.success(res.data?.message || "Deleted Successfully");
        setConfirmOpen(false);
        setPendingDeleteIds([]);
        await fetchDropdown();
        await fetchList(1, debouncedSearch, selectedDeptId);
      } else {
        toast.error(res.data?.message || "Departments having reference cannot be deleted");
      }
    } catch (err) {
      console.error("dept_delete_selected error:", err);
      toast.error(err.response?.data?.message || "Server error");
    } finally {
      setIsDeleting(false);
    }
  };

  // ---------------- UI classes ----------------
  const inputClass = (hasError) =>
    `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
      hasError ? "border-red-500" : ""
    } ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`;

  const selectClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;

  return (
    <AdminLayoutWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">DEPARTMENT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ============ FORM VIEW (Add/Edit) ============ */}
          {showForm ? (
            <>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={closeForm}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isDark
                        ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                        : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                    }`}
                  >
                    <FaChevronLeft />
                    Back
                  </button>

                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {isEdit ? `Edit Department (ID: ${editId})` : "Add New Department"}
                  </span>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleSubmit}
                    disabled={saving}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                  >
                    <FaCheck />
                    {saving ? "Saving..." : "Save"}
                  </button>

                  <button
                    onClick={closeForm}
                    disabled={saving}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isDark ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                    } disabled:opacity-60`}
                  >
                    <FaTimes />
                    Cancel
                  </button>
                </div>
              </div>

              <div className={`rounded-xl border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                <div className={`${isDark ? "bg-gray-800" : "bg-gray-50"} p-6`}>
                  <div className="max-w-2xl mx-auto space-y-4">
                    <div className="grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-4">
                        <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          Department Name <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-8">
                        <input
                          type="text"
                          value={formData.deptName}
                          onChange={(e) => handleFormChange(e.target.value)}
                          disabled={saving}
                          className={inputClass(!!errors.deptName)}
                          placeholder="Enter Department Name..."
                        />
                        {errors.deptName && <p className="text-red-500 text-sm mt-1">{errors.deptName}</p>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* ============ LIST VIEW (Search + Table) ============ */
            <>
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                  <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                    Department Listing
                  </h1>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {totalRecords} Records
                  </span>
                </div>

                <button
                  onClick={openAddForm}
                  className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaPlus />
                  Add New
                </button>
              </div>

              {/* Filters */}
              <div
                className={`rounded-xl border mb-6 overflow-hidden ${
                  isDark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div className={`p-6 ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div>
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Departments
                      </label>
                      <select
                        value={selectedDeptId}
                        onChange={(e) => setSelectedDeptId(e.target.value)}
                        className={selectClass}
                      >
                        <option value="">Select</option>
                        {deptOptions.map((d) => (
                          <option key={d.fld_deptid} value={String(d.fld_deptid)}>
                            {d.fld_departmentname}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Search (Department Name)
                      </label>
                      <input
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder="Type to search department..."
                        className={inputClass(false)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div
                className={`rounded-xl shadow-sm border overflow-hidden ${
                  isDark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={`${buttonClass} text-white`}>
                      <tr>
                        <th className="px-4 py-4 text-center font-semibold border-r border-white/20">S.No</th>
                        <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Department Name</th>
                        <th className="px-4 py-4 text-center font-semibold">Select</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {loading ? (
                        <tr>
                          <td
                            colSpan="3"
                            className={`px-4 py-6 text-center text-sm font-medium ${
                              isDark ? "text-gray-300" : "text-gray-600"
                            }`}
                          >
                            Loading...
                          </td>
                        </tr>
                      ) : records.length === 0 ? (
                        <tr>
                          <td
                            colSpan="3"
                            className={`px-4 py-6 text-center text-sm font-medium ${
                              isDark ? "text-gray-300" : "text-gray-600"
                            }`}
                          >
                            No records found
                          </td>
                        </tr>
                      ) : (
                        records.map((record, index) => (
                          <tr
                            key={record.id}
                            className={`transition-colors duration-200 ${
                              index % 2 === 0
                                ? isDark
                                  ? "bg-gray-800"
                                  : "bg-gray-50"
                                : isDark
                                ? "bg-gray-900"
                                : "bg-white"
                            }`}
                          >
                            <td
                              className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {record.sNo}
                            </td>

                            <td
                              className={`px-4 py-3 text-sm text-left border-r border-gray-200 dark:border-gray-700 font-medium ${
                                isDark ? "text-blue-300" : "text-blue-600"
                              }`}
                            >
                              <button
                                type="button"
                                onClick={() => openEditForm(record)}
                                className="underline hover:opacity-80"
                                title="Click to edit"
                              >
                                {record.name}
                              </button>
                            </td>

                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={!!record.selected}
                                onChange={(e) => handleSelectRecord(record.id, e.target.checked)}
                                className={`w-4 h-4 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
                              />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              <div className="flex justify-start mt-6">
                <div className="flex gap-1 flex-wrap">
                  {renderPageNumbers.map((page, index) => (
                    <button
                      key={index}
                      onClick={() => handlePageChange(page)}
                      disabled={page === "..."}
                      className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                        page === currentPage
                          ? "bg-blue-600 text-white border-blue-600"
                          : page === "..."
                          ? isDark
                            ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                            : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                          : isDark
                          ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              </div>

              {/* Remove */}
              <div className="flex justify-end mt-4">
                <button
                  onClick={openDeleteConfirm}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark ? "bg-red-700 hover:bg-red-600 text-white" : "bg-red-600 hover:bg-red-700 text-white"
                  }`}
                >
                  <FaTrash />
                  Remove
                </button>
              </div>
            </>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-confirm-title"
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeDeleteConfirm}
          />

          <div
            className={`relative w-full max-w-md rounded-2xl border shadow-2xl overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-gray-900 to-gray-800 border-white/10 text-white"
                : "bg-white border-gray-200 text-gray-900"
            }`}
          >
            <div className={`h-1.5 w-full ${buttonClass}`} />

            <button
              type="button"
              onClick={closeDeleteConfirm}
              className={`absolute top-3 right-3 p-1.5 rounded-lg transition ${
                isDark ? "hover:bg-white/10 text-white/70" : "hover:bg-gray-100 text-gray-500"
              }`}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-600/15 text-red-500">
                  <FaExclamationTriangle className="text-xl" />
                </div>
                <div>
                  <h3 id="delete-confirm-title" className="text-lg font-semibold">
                    Delete Department
                  </h3>
                  <p className={`mt-2 text-sm leading-6 ${isDark ? "text-white/70" : "text-gray-600"}`}>
                    Are you sure you want to delete{" "}
                    <span className="font-semibold">
                      {pendingDeleteIds.length} department{pendingDeleteIds.length > 1 ? "s" : ""}
                    </span>
                    ? This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeDeleteConfirm}
                  disabled={isDeleting}
                  className={`px-4 py-2 rounded-xl text-sm font-medium border transition ${
                    isDark
                      ? "border-white/15 bg-white/5 hover:bg-white/10 text-white"
                      : "border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700"
                  } disabled:opacity-50`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteSelected}
                  disabled={isDeleting}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 shadow transition hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                >
                  <FaTrash />
                  {isDeleting ? "Removing..." : "Remove"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayoutWrapper>
  );
};

export default DepartmentListing;